import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function CategoriesSection() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (url: string, nameKey: string) => {
    setSelectedCategory(t(nameKey));
    setIsOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="relative py-12 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-8 md:mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
            {t('category_title')}
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
            {t('category_subtitle')}
          </p>
        </div>

        <div className="flex justify-center mb-10 md:mb-12">
          <div ref={dropdownRef} className="relative w-full max-w-md">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full flex items-center justify-between gap-3 px-5 py-3.5 bg-white border border-background-200 rounded-xl text-foreground-800 hover:border-primary-300 transition-all duration-200 text-sm cursor-pointer"
            >
              <span className={selectedCategory ? 'text-foreground-900' : 'text-foreground-400'}>
                {selectedCategory || t('select_category')}
              </span>
              <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {isOpen && (
              <div className="absolute z-50 mt-2 w-full bg-white border border-background-200 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.nameKey}
                    onClick={() => handleSelect(cat.url, cat.nameKey)}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 cursor-pointer border-b border-background-100 last:border-b-0"
                  >
                    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={cat.image} alt={t(cat.nameKey)} className="w-full h-full object-cover" />
                    </div>
                    <span>{t(cat.nameKey)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.nameKey}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col bg-white border border-background-200 hover:border-primary-300 rounded-xl overflow-hidden transition-all duration-300 cursor-pointer hover:bg-background-50"
            >
              <div className="relative w-full h-28 md:h-32 overflow-hidden">
                <img
                  src={cat.image}
                  alt={t(cat.nameKey)}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent"></div>
                <div className="absolute bottom-2 left-2 flex items-center gap-1.5">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-white/90 text-primary-600">
                    <i className={`${cat.icon} w-3.5 h-3.5 flex items-center justify-center`}></i>
                  </div>
                </div>
              </div>
              <div className="px-3 py-3">
                <span className="text-xs md:text-sm font-medium text-foreground-700 group-hover:text-foreground-900 text-center block">
                  {t(cat.nameKey)}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}